import { Language } from './../../core/Languages';
import { environment } from './../../environments/environment';
import { Inject, Injectable } from "@angular/core";
import {
    IPublicClientApplication,
    AccountInfo,
    EndSessionRequest,
    AuthenticationResult,
    PopupRequest,
    SilentRequest,
    RedirectRequest
} from "@azure/msal-browser";
import { MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG } from "./constants";
import { Observable, from, EMPTY } from 'rxjs';
import { Location } from '@angular/common';
import { IMsalService } from "./i-msal-service";
import { catchError, map, tap } from "rxjs/operators";
import { MsalInterceptorConfig } from './msal-interceptor-config';

@Injectable()
export class MsalService implements IMsalService {
       
    private redirectHash: string;
    private userLanguage: string;

    constructor(
        @Inject(MSAL_INSTANCE) public instance: IPublicClientApplication,
        @Inject(MSAL_INTERCEPTOR_CONFIG) private msalInterceptorConfig: MsalInterceptorConfig,
        private location: Location
    ) {
        // Cache the code hash before Angular router clears it
        const path = this.location.path(true);
        if (path.includes("#")) {
            const hash = path.split('#').pop();
            if (hash) {
                this.redirectHash = `#${hash}`;
            }
        }
    }

    acquireTokenPopup(request: PopupRequest): Observable<AuthenticationResult> {
        return from(this.instance.acquireTokenPopup(request));
    }
    acquireTokenRedirect(request: RedirectRequest): Observable<void> {
        return from(this.instance.acquireTokenRedirect(request));
    }
    acquireTokenSilent(silentRequest: SilentRequest): Observable<AuthenticationResult> {
        return from(this.instance.acquireTokenSilent(silentRequest));
    }
    handleRedirectObservable(): Observable<AuthenticationResult> {
        return from(this.instance.handleRedirectPromise(this.redirectHash));
    }
    loginPopup(request?: PopupRequest): Observable<AuthenticationResult> {
        return from(this.instance.loginPopup(request));
    }

    loginRedirect(request?: RedirectRequest): Observable<void> {
        const externalQueryParam = { };
        externalQueryParam["ui_locales"]=this.selectUiLocale(this.userLanguage);
        if(!request)
        {
            request=  {  } as RedirectRequest;
        }
        request.extraQueryParameters = externalQueryParam
        return from(this.instance.loginRedirect(request));
    }

    logout(logoutRequest?: EndSessionRequest): Observable<void> {
        return from(this.instance.logoutRedirect(logoutRequest))
    }
           
    ssoSilent(request: SilentRequest): Observable<AuthenticationResult> {
        return from(this.instance.ssoSilent(request));
    }

    resetPassword(userEmail: string) {
        const externalQueryParam = { };
        externalQueryParam["email"] = encodeURIComponent(userEmail);
        externalQueryParam["ui_locales"] = this.selectUiLocale(this.userLanguage);
        const request: RedirectRequest = {
            scopes: ["openid"],
            authority: environment.msal.resetPasswordAuthority,
            extraQueryParameters: externalQueryParam 
        };
        return from(this.instance.loginRedirect(request));
    }

    isLoggedIn(): boolean {
        const isLoggedIn = !!(this.instance.getActiveAccount() || this.instance.getAllAccounts()[0]);        
        return isLoggedIn        
    }

    private get account(): AccountInfo {
        return this.instance.getActiveAccount() || this.instance.getAllAccounts()[0];        
    }

    acquireAccessTokenSilent(scopes?:string[]): Observable<string> {
        const req: SilentRequest = { scopes: scopes, account: this.account, };
        return this.acquireTokenSilent(req)
        .pipe(
            catchError((err) => {  
                console.error(err);                 
                const redirectStartPage = window.location.href;
                this.acquireTokenRedirect({ ...this.msalInterceptorConfig.authRequest, scopes, redirectStartPage });
                return EMPTY;
            }),
            map((result: AuthenticationResult) => {
                return result.accessToken;
            })
        );
    }

    acquireIdTokenSilent(scopes?:string[]): Observable<string> {
        const req: SilentRequest = { scopes: scopes, account: this.account, };
        return this.acquireTokenSilent(req)
        .pipe(
            catchError((err) => {  
                console.error(err);                 
                const redirectStartPage = window.location.href;
                this.acquireTokenRedirect({ ...this.msalInterceptorConfig.authRequest, scopes, redirectStartPage });
                return EMPTY;
            }),
            map((result: AuthenticationResult) => {
                return result.idToken;
            })
        );
    }

    private get scopes(): Array<string> {
        return Object.values(environment.msal.protectedResources).map(r => r.scopes).reduce((acc, val) => acc.concat(val), []);
    }

    selectUiLocale(language: string): any {
        switch (language) {
            case "itIT":
                return "it";
            case "esES":
                return "es";
            case "plPL":
                return "pl";
            default:
                return "en"
        }
    }

    updateLanguage(langSelected: string) {
        this.userLanguage = langSelected;

    }

}


